import type { Zrm_RenterProfile_Attributes, Zrm_RenterProfile_PetDetails } from '../types/GraphqlTypes';
import type {
  SatelliteWebLandlordGetMessagesRenterProfileAttributesDto,
  SatelliteWebLandlordGetMessagesRenterProfilePetDetailsDto,
  SatelliteWebLandlordLeadManagementTableRenterProfileAttributesDto,
  SatelliteWebLandlordLeadManagementTableRowRenterProfilePetDetailsDto,
} from '@zg-rentals/ts-satellite-client';

type RenterProfileAttributes =
  | Zrm_RenterProfile_Attributes
  | SatelliteWebLandlordLeadManagementTableRenterProfileAttributesDto
  | SatelliteWebLandlordGetMessagesRenterProfileAttributesDto;

type PetDetail =
  | Zrm_RenterProfile_PetDetails
  | SatelliteWebLandlordLeadManagementTableRowRenterProfilePetDetailsDto
  | SatelliteWebLandlordGetMessagesRenterProfilePetDetailsDto;

/** Union of valid attribute keys across both GraphQL and Proxit DTO types. */
type AttributeKey =
  // `reasonForMoving` was deleted from the BE; exclude it from the GraphQL type until it's dropped from the gateway schema.
  | Exclude<keyof Zrm_RenterProfile_Attributes, '__typename' | 'updatedAtMs' | 'reasonForMoving'>
  | keyof SatelliteWebLandlordLeadManagementTableRenterProfileAttributesDto
  | Exclude<keyof SatelliteWebLandlordGetMessagesRenterProfileAttributesDto, 'updatedAtMs'>;

const ATTRIBUTE_TO_TITLE_MAP: Record<AttributeKey, string> = {
  moveInDate: 'Ideal move in date',
  introduction: 'Introduction',
  numOccupants: 'Total occupants',
  numBedrooms: 'Total bedrooms desired',
  credit: 'Credit score',
  jobTitle: 'Job title',
  incomeYearly: 'Annual income', // Deprecate
  incomeYearlyRange: 'Annual income',
  voucherAmountMonthly: 'Housing voucher amount',
  isSmoker: 'Smoker',
  isSmokerBoolean: 'Smoker',
  hasPets: 'Has pets',
  petDetails: 'Pets',
  desiredParking: 'Parking needed',
  leaseLengthMonths: 'Desired lease duration',
};

type RenterProfileAttribute = {
  title: string;
  value: string;
};

/**
 * @description Takes in renter profile attributes data and returns an array of RenterProfileAttribute for the Renter Profile column
 */
export const getFormattedAttributesList = (
  data: RenterProfileAttributes | null | undefined,
): Array<RenterProfileAttribute> => {
  if (!data) {
    return [];
  }

  const formattedAttributesList: Array<RenterProfileAttribute> = [];

  for (const [key, value] of Object.entries(ATTRIBUTE_TO_TITLE_MAP)) {
    if (!(key in data)) continue;
    const currentValue = data[key as keyof RenterProfileAttributes];

    if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
      let valueToAdd = `${currentValue}`;
      let skipAdd = false;

      if (key === 'petDetails' && Array.isArray(currentValue)) {
        valueToAdd = value.length ? getFormattedPetDetails(currentValue) : '';

        if (!valueToAdd) {
          skipAdd = true;
        }
      } else if (key === 'isSmoker' || key === 'isSmokerBoolean') {
        valueToAdd = currentValue ? 'Yes' : 'No';
      } else if (key === 'leaseLengthMonths') {
        valueToAdd = `${currentValue} month(s)`;
      }

      if (!skipAdd) {
        formattedAttributesList.push({
          title: value,
          value: valueToAdd,
        });
      }
    }
  }

  return formattedAttributesList;
};

/**
 * @description Formats a list of pet details into a string
 * Ex: Large Dog (Labrador 50 lbs), Other (fish)
 */
export const getFormattedPetDetails = (petDetails: Array<PetDetail | null>): string => {
  const allPetDetails: Array<string> = [];

  petDetails?.forEach((petDetail) => {
    const { breed, description, size, type, weightPounds } = petDetail || {};

    if (!type) {
      return '';
    }

    const prefix = size ? `${size} ${type}` : type;
    const additionalAttributes = [];

    if (breed) {
      additionalAttributes.push(breed);
    }

    if (description) {
      additionalAttributes.push(description);
    }

    if (weightPounds) {
      additionalAttributes.push(weightPounds);
    }

    const details = additionalAttributes.length ? `${prefix} (${additionalAttributes.join(' ')})` : prefix;
    allPetDetails.push(details);
  });

  return allPetDetails?.join(', ');
};
